/**
 * Every read and write the app performs, each one a single transaction.
 *
 * §11.3: "Every write is ONE transaction. The 'I injected' row is a single `add`
 * on `log`, so it cannot clobber settings and settings cannot clobber it."
 */

import { DELETE_CONFIRM_WINDOW_HOURS, SCHEMA_VERSION } from '../config.js';
import { isInjection } from '../core/types.js';
import type {
  Injection,
  LogRow,
  Reading,
  RoundingMode,
  Settings,
  Tombstone,
} from '../core/types.js';
import type { SettingsPeriod } from '../core/periods.js';
import { hasRowInsideWindow } from '../core/history.js';
import {
  ACK_KEY,
  HISTORY_SCOPE,
  META_KEY,
  SETTINGS_KEY,
  SETTINGS_SCOPE,
  STORE,
  ackKeyForMode,
  ackKeyForSetting,
} from './schema.js';
import type {
  AckRow,
  BackupRow,
  CalibrationRow,
  VesselRow,
  DisplayRow,
  DosingHistoryRow,
  EnvelopeRow,
  InstallRow,
  LanguageRow,
  LogRevisionRow,
  RecoveryBlock,
  SettingsHistoryRow,
  SettingsRow,
} from './schema.js';
import { readLogRow } from './envelope.js';
import { add, clear, get, getAll, maxKey, put, remove, runTransaction } from './tx.js';

const NO_REVISION = 0;

/** §11.3 v23 — the counter, plus the two provenance stamps §7.5 needs. */
async function readLogRevisionRow(tx: IDBTransaction): Promise<LogRevisionRow> {
  const row = await get<LogRevisionRow>(tx, STORE.meta, META_KEY.logRevision);
  return (
    row ?? {
      key: META_KEY.logRevision,
      logRevision: NO_REVISION,
      lastImportAtMs: null,
      lastLocalInjectionAtMs: null,
    }
  );
}

/**
 * Bumps the counter every consumer polls. §7.9 v22: without it "a second tab
 * goes on rendering a result whose §7.4 stacking line cites an injection that no
 * longer exists, and whose §10.5 band E form was chosen by reading two stores
 * that are now empty".
 */
async function bumpLogRevision(
  tx: IDBTransaction,
  patch: Partial<Omit<LogRevisionRow, 'key' | 'logRevision'>> = {},
): Promise<number> {
  const current = await readLogRevisionRow(tx);
  const next = current.logRevision + 1;
  await put(tx, STORE.meta, { ...current, ...patch, key: META_KEY.logRevision, logRevision: next });
  return next;
}

export interface StoredState {
  readonly settings: Settings | null;
  readonly settingsHistory: readonly SettingsPeriod[];
  readonly log: readonly LogRow[];
  readonly readings: readonly Reading[];
  readonly logRevision: number;
  readonly installedAtMs: number;
  readonly lastImportAtMs: number | null;
  readonly lastLocalInjectionAtMs: number | null;
  /**
   * §11.3 — stored rows that failed re-validation ON LOAD and were dropped.
   * Counted rather than merely discarded, because §7.5's doctrine is that a log
   * the app had to prune must never silently read as "no recent insulin".
   */
  readonly droppedStoredRows: number;
  readonly acks: ReadonlySet<string>;
  readonly dosingHistory: DosingHistoryRow;
  readonly lastJsonExportAtMs: number | null;
  /**
   * `10a` — the stored language choice, or `null` when there is none.
   *
   * **NULL RATHER THAN A DEFAULT, and the schema comment says why**: a database
   * with no row has not CHOSEN English, it has not been asked. Those are the
   * same thing to render and a different thing to reason about, and a storage
   * layer that collapses them has thrown away the distinction before anything
   * can use it. The interface applies `DEFAULT_LANGUAGE`; this reports what is
   * there.
   */
  readonly languageChoice: LanguageRow | null;
  /** Phase 2 — the reader's own grams per food, or null if they have set none. */
  readonly calibration: CalibrationRow | null;
  /** How large the reader asked for the type, or null if they never asked. */
  readonly display: DisplayRow | null;
  /**
   * T31 — the reader's own vessels, as ratios, or null if they have set none.
   *
   * Absent reads as "nothing calibrated", which is exactly today's behaviour:
   * a ratio of 1 reproduces the table. That is what lets this feature ship
   * without a default that could make anything worse.
   */
  readonly vessel: VesselRow | null;
}

/** One read of everything the reducer needs, in one transaction. */
export async function readAll(db: IDBDatabase, nowMs: number): Promise<StoredState> {
  return runTransaction(
    db,
    [STORE.meta, STORE.settings, STORE.acks, STORE.log, STORE.readings, STORE.settingsHistory],
    'readonly',
    async (tx) => {
      const [
        settingsRow, history, log, readings, acks, revision, install, dosing, backup,
        language, calibration, display, vessel,
      ] =
        await Promise.all([
          get<SettingsRow>(tx, STORE.settings, SETTINGS_KEY),
          getAll<SettingsHistoryRow>(tx, STORE.settingsHistory),
          getAll<LogRow>(tx, STORE.log),
          getAll<Reading>(tx, STORE.readings),
          getAll<AckRow>(tx, STORE.acks),
          readLogRevisionRow(tx),
          get<InstallRow>(tx, STORE.meta, META_KEY.install),
          get<DosingHistoryRow>(tx, STORE.meta, META_KEY.dosingHistory),
          get<BackupRow>(tx, STORE.meta, META_KEY.backup),
          get<LanguageRow>(tx, STORE.meta, META_KEY.language),
          get<CalibrationRow>(tx, STORE.meta, META_KEY.calibration),
          get<DisplayRow>(tx, STORE.meta, META_KEY.display),
          get<VesselRow>(tx, STORE.meta, META_KEY.vessel),
        ]);

      const settings: Settings | null =
        settingsRow === undefined
          ? null
          : {
              revision: settingsRow.revision,
              target: settingsRow.target,
              isf: settingsRow.isf,
              icr: settingsRow.icr,
              roundingMode: settingsRow.roundingMode,
              threshold: settingsRow.threshold,
              basalName: settingsRow.basalName,
              basalUnits: settingsRow.basalUnits,
              basalTiming: settingsRow.basalTiming,
              // `''` here is `UNANSWERED_INSULIN` and makes §8.5 ask. No
              // write path produces it: the question precedes the first commit
              // and "I don't know" is `UNKNOWN_INSULIN`. Read straight through,
              // so a hand-edited row reaches the gate instead of a guess.
              bolusId: settingsRow.bolusId,
              eatDelayMinutes: settingsRow.eatDelayMinutes,
              personName: settingsRow.personName,
            };

      // §11.3 — "Re-validate on every LOAD and import." The import half was
      // implemented; this is the load half. A row that fails is DROPPED rather
      // than repaired, because repairing it would invent a dose — and counted,
      // so §7.5 can refuse to call the remaining log trustworthy.
      const validated: LogRow[] = [];
      for (const row of log) {
        const checked = readLogRow(row);
        if (checked !== null) validated.push(checked);
      }

      return {
        settings,
        // No translation left: `SettingsHistoryRow` and §7.7's `SettingsPeriod`
        // have had the same shape since the stored row stopped spelling
        // `roundingMode` as `mode`. This was a `map` that renamed one field and
        // defaulted another. If the two shapes diverge again, the translation
        // comes back HERE — this is the boundary, empty or not.
        settingsHistory: history,
        log: validated,
        droppedStoredRows: log.length - validated.length,
        readings,
        logRevision: revision.logRevision,
        // `?? nowMs` is not a shrug at a row that might be missing. The row
        // is written in `onupgradeneeded`, so it exists for every database
        // that exists and this branch means the database is already broken.
        // WHICH default it picks is the safety decision: §7.5 marks a log
        // predating the install as `suspect`, so `nowMs` makes every existing
        // row suspect, where `0` would make every row trusted. When provenance
        // cannot be established, claiming less of it is the only safe way to
        // be wrong.
        installedAtMs: install?.installedAtMs ?? nowMs,
        lastImportAtMs: revision.lastImportAtMs,
        // No translation here any more. The stored key WAS `lastLocalWriteAtMs`
        // and this line existed to rename it on the way past; the store says
        // `lastLocalInjectionAtMs` now, which is what note 7 established the
        // value actually means — only an injection append stamps it.
        lastLocalInjectionAtMs: revision.lastLocalInjectionAtMs,
        acks: new Set(acks.map((row) => row.key)),
        // §6.7 v19 — A MISSING ROW READS AS `unanswered`. The row is not seeded
        // at database creation: an install predating the feature, or a partial
        // restore, must ask rather than assume.
        dosingHistory: dosing ?? {
          key: META_KEY.dosingHistory,
          state: 'unanswered',
          text: '',
          answeredAtMs: null,
        },
        lastJsonExportAtMs: backup?.lastJsonExportAtMs ?? null,
        // `10a` — reported as it is, `null` and all. The row is not seeded at
        // database creation, so its absence means the question has not been
        // asked, and deciding what that RENDERS as is the interface's business
        // rather than this function's. Unlike the dosing note above, which has
        // three states of its own and defaults to the one that keeps asking.
        languageChoice: language ?? null,
        calibration: calibration ?? null,
        // A reader who has never opened the setting has no row, and the
        // interface supplies TEXT_SCALE_DEFAULT. Unlike language, there is no
        // distinction to preserve: not choosing a size and choosing the normal
        // size are the same thing to a reader and to the renderer.
        display: display ?? null,
        vessel: vessel ?? null,
      } satisfies StoredState;
    },
  );
}

// ─── settings ───────────────────────────────────────────────────────────────

export interface SettingsCommit {
  readonly target: number;
  readonly isf: number;
  readonly icr: number;
  readonly roundingMode: RoundingMode;
  /** §8.5 — a row id from `src/data/insulins.ts`, or one of the two sentinels. */
  readonly bolusId: string;
  /** §8.5 — the brand in words, for the recovery block a human copies down. */
  readonly bolusName: string;
  /** §8.5 — the reader's own pre-meal wait, or null for the class range. */
  readonly eatDelayMinutes: number | null;
  readonly threshold: number;
  readonly basalName: string;
  readonly basalUnits: number;
  readonly basalTiming: string;
  /** Optional; '' means not given. See `Settings.personName`. */
  readonly personName: string;
  /** Acknowledgement keys this commit earns — §4.5's confirm-once and §5.1's gate. */
  readonly acknowledged: readonly string[];
  readonly nowMs: number;
}

/**
 * §7.7 — does this commit change anything a prescription PERIOD records?
 *
 * **Derived from the stored row's own keys rather than from a list here**, and
 * that is the whole design. The obvious version is a hand-written list — *did
 * target, isf, icr, mode or insulin change?* — and it rots in the unsafe
 * direction: the day a sixth field joins `settingsHistory` and nobody adds it
 * here, a real prescription change stops starting a new period and the export
 * attributes doses to settings that did not produce them. Silently, and with no
 * test to notice, because the tests would have been written against the five
 * fields that existed at the time.
 *
 * Reading the row's keys means adding a field to the type joins this
 * comparison automatically. `check-plan.py` asserts that the two agree, with a
 * seeded mutation proving it fails when one is missed.
 *
 * `revision`, `changedAtMs` and `imported` are excluded because they are
 * PROVENANCE about the row rather than the prescription it records — comparing
 * them would make every commit look like a change, which is the behaviour this
 * replaces.
 */
const PERIOD_PROVENANCE: readonly (keyof SettingsHistoryRow)[] = ['revision', 'changedAtMs', 'imported'];

function sameProvenance(previous: SettingsHistoryRow, commit: SettingsCommit): boolean {
  const proposed: SettingsHistoryRow = {
    revision: previous.revision,
    changedAtMs: previous.changedAtMs,
    target: commit.target,
    isf: commit.isf,
    icr: commit.icr,
    roundingMode: commit.roundingMode,
    bolusId: commit.bolusId,
    imported: previous.imported,
  };
  return (Object.keys(proposed) as (keyof SettingsHistoryRow)[])
    .filter((field) => !PERIOD_PROVENANCE.includes(field))
    .every((field) => proposed[field] === previous[field]);
}

function recoveryFor(commit: SettingsCommit): RecoveryBlock {
  // §11.3 — fixed field names and EXPLICIT UNITS. "A parsed integer does not
  // reveal whether 150 means units, hundredths or mg/dL", and the field name is
  // the only thing that says which.
  return {
    // §11.3 — the recovery block is what he copies down off the fail-closed
    // screen, so whose record it is belongs on it.
    personName: commit.personName,
    targetMgDl: commit.target,
    oneUnitLowersMgDl: commit.isf,
    oneUnitCoversGramsCarbohydrate: commit.icr,
    basalName: commit.basalName,
    basalUnitsPerDay: commit.basalUnits,
    basalTiming: commit.basalTiming,
    // The BRAND, not the id. This block is read off a screen and typed back in
    // by a person; `novorapid` is not what the box says and not what they would
    // recognise, which is the same reason the units are spelled out beside
    // every number above.
    bolusName: commit.bolusName,
  };
}

/**
 * §11.3's ALLOCATION rule, which v11 lacked and which produced two deterministic
 * collisions on paths this document legislates:
 *
 * > The next `revision` is `max(settingsHistory keys) + 1`, read inside the same
 * > transaction — **never** by incrementing the value stored on the settings
 * > record. On a virgin install the store is empty and the base case is
 * > explicit: `(max ?? 0) + 1`, so the first commit takes 1.
 *
 * ADOPTION ALLOCATES: adopting an imported setting is a settings commit and
 * takes a fresh revision, because the imported entry carries another install's
 * `changedAtMs` and `settingsHistory` is never adopted.
 *
 * **A commit that changes nothing `settingsHistory` records keeps the current
 * revision** — `BACKLOG` T18. §7.7 has said so since v10 ("a threshold-only
 * change does not bump it") and nothing implemented it, so editing only the
 * threshold, the basal block, the reader's name or §8.5's pre-meal wait started
 * a fresh prescription period identical to the one before it. §7.7.1's export
 * then printed two sections with the same ratios and the doses split between
 * them, in the document whose whole argument is that it does not misstate what
 * produced a row.
 *
 * Nothing about a dose was ever wrong: the row stamp stayed accurate and every
 * period it could point at held identical ratios. It was noise, in a clinical
 * document.
 */
export function commitSettings(db: IDBDatabase, commit: SettingsCommit): Promise<number> {
  return runTransaction(db, SETTINGS_SCOPE, 'readwrite', async (tx) => {
    const current = await get<SettingsRow>(tx, STORE.settings, SETTINGS_KEY);
    const previous =
      current === undefined
        ? undefined
        : await get<SettingsHistoryRow>(tx, STORE.settingsHistory, current.revision);
    const unchanged = previous !== undefined && sameProvenance(previous, commit);

    const highest = await maxKey(tx, STORE.settingsHistory);
    // The current revision when nothing the history records moved. `current`
    // is non-undefined whenever `unchanged` is, which the narrowing above
    // establishes and this expression relies on.
    const revision = unchanged ? (current?.revision ?? NO_REVISION) : (highest ?? NO_REVISION) + 1;

    const settings: SettingsRow = {
      key: SETTINGS_KEY,
      revision,
      personName: commit.personName,
      target: commit.target,
      isf: commit.isf,
      icr: commit.icr,
      roundingMode: commit.roundingMode,
      threshold: commit.threshold,
      basalName: commit.basalName,
      basalUnits: commit.basalUnits,
      basalTiming: commit.basalTiming,
      bolusId: commit.bolusId,
      eatDelayMinutes: commit.eatDelayMinutes,
    };
    await put(tx, STORE.settings, settings);

    // `add`, never `put`, so an allocation bug fails loud instead of silently
    // rewriting provenance — which is also why the no-change case must not
    // reach it at all: `add` on an existing key rejects, and it should.
    if (!unchanged) {
      await add(tx, STORE.settingsHistory, {
        revision,
        changedAtMs: commit.nowMs,
        target: commit.target,
        isf: commit.isf,
        icr: commit.icr,
        roundingMode: commit.roundingMode,
        bolusId: commit.bolusId,
        imported: false,
      } satisfies SettingsHistoryRow);
    }

    // The recovery block is kept synchronised with committed settings, in the
    // same transaction, so the fail-closed screen can never show a stale
    // prescription.
    const envelope = await get<EnvelopeRow>(tx, STORE.meta, META_KEY.envelope);
    await put(tx, STORE.meta, {
      key: META_KEY.envelope,
      schemaVersion: envelope?.schemaVersion ?? SCHEMA_VERSION,
      recovery: recoveryFor(commit),
    } satisfies EnvelopeRow);

    for (const key of commit.acknowledged) {
      await put(tx, STORE.acks, { key: key, acknowledgedAtMs: commit.nowMs } satisfies AckRow);
    }
    return revision;
  });
}

export function acknowledge(db: IDBDatabase, key: string, nowMs: number): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.acks], 'readwrite', (tx) =>
    put(tx, STORE.acks, { key: key, acknowledgedAtMs: nowMs } satisfies AckRow),
  );
}

export const ackKeys = { ...ACK_KEY, forMode: ackKeyForMode, forSetting: ackKeyForSetting };

// ─── the log ────────────────────────────────────────────────────────────────

export type LogWriteOutcome =
  | { readonly kind: 'written'; readonly logRevision: number }
  /** §7.2 — a retry never creates a second row and never moves the timestamp. */
  | { readonly kind: 'already_written'; readonly logRevision: number };

/**
 * §7.2's commit, and §11.3's mismatch policy at this exact point.
 *
 * v5 applied one rule to both commit points — "mismatch → abort, invalidate,
 * recompute" — which is right at a reveal and UNIMPLEMENTABLE here in either
 * reading: abort and discard the tap and a PHYSICAL INJECTION GOES UNRECORDED,
 * leaving the gate blind; or treat the abort as a write failure and the retry
 * loops forever, because a revision mismatch is permanent.
 *
 * So: **always commit the append.** A keyed single `add` cannot clobber
 * anything, and the revision bump rides in the same transaction. The displayed
 * result is invalidated afterwards, by the caller.
 *
 * "The injection is never discarded and its units are never replaced. The event
 * is frozen at the tap (§7.2); only the RESULT is invalidated."
 */
export async function appendInjection(
  db: IDBDatabase,
  row: Injection,
  nowMs: number,
): Promise<LogWriteOutcome> {
  return runTransaction(db, HISTORY_SCOPE, 'readwrite', async (tx) => {
    const existing = await get<LogRow>(tx, STORE.log, row.id);
    if (existing !== undefined) {
      // Idempotent on `id`. Two taps cannot log 52 units.
      const revision = await readLogRevisionRow(tx);
      return { kind: 'already_written', logRevision: revision.logRevision } satisfies LogWriteOutcome;
    }
    await add(tx, STORE.log, row);
    const logRevision = await bumpLogRevision(tx, { lastLocalInjectionAtMs: nowMs });
    return { kind: 'written', logRevision } satisfies LogWriteOutcome;
  });
}

/**
 * §7.8 — a reading is its own event, and it bumps the SAME counter.
 *
 * **It does NOT stamp `lastLocalInjectionAtMs`, and the omission is the point.**
 * It used to, which made a reading an input to §7.5's provenance and therefore
 * to a §11.2 snapshot field — forbidden by §7.8 in as many words: *"Not an
 * input to anything. Readings are absent from §11.2's dosing snapshot, from
 * §6.5's carbohydrate baseline, and from §7.4's stacking gate — a reading is
 * not an injection. The one exception is §10.5's band E derivation."*
 *
 * The effect was that recording a blood sugar after an import flipped
 * provenance from suspect to trusted, suppressing §7.5's *"No recent dose
 * recorded"* caveat on an install that had never once watched him inject. The
 * commonest reading is the one offered after a band C/D block — a session in
 * which dosing is structurally impossible — so the rows that bought the trust
 * were the least entitled to.
 *
 * The stamp rode along because §7.8 says a reading bumps the same counter, and
 * the shared `bumpLogRevision` call carried the patch with it. The bump stays;
 * §11.2 requires it. Only the stamp goes.
 */
export function appendReading(db: IDBDatabase, reading: Reading): Promise<number> {
  return runTransaction(db, HISTORY_SCOPE, 'readwrite', async (tx) => {
    const existing = await get<Reading>(tx, STORE.readings, reading.id);
    if (existing === undefined) await add(tx, STORE.readings, reading);
    return bumpLogRevision(tx);
  });
}

export type DeleteResult =
  | { readonly kind: 'tombstoned'; readonly logRevision: number }
  | { readonly kind: 'removed'; readonly logRevision: number }
  | { readonly kind: 'not_found' };

/**
 * §7.3 — delete only, with consequence.
 *
 * **Only deletions inside `DELETE_CONFIRM_WINDOW_HOURS` leave a tombstone.**
 * Outside the window there is no gate to bypass, and a tombstone for every tidied
 * old row would bury the signal it exists to show.
 *
 * The tombstone stands where the dose stood — same `id`, same `timestamp` — and
 * carries nothing else. **The dose values do not survive, and that is the point
 * rather than an economy**: §7.3 exists because deleting legitimately means "I
 * never injected this", and keeping the amount would re-create the ambiguity the
 * deletion resolved.
 */
export function deleteLogRow(db: IDBDatabase, id: string, nowMs: number): Promise<DeleteResult> {
  return runTransaction(db, HISTORY_SCOPE, 'readwrite', async (tx) => {
    const row = await get<LogRow>(tx, STORE.log, id);
    if (row === undefined || !isInjection(row)) return { kind: 'not_found' } satisfies DeleteResult;

    const insideWindow = hasRowInsideWindow([row], nowMs, DELETE_CONFIRM_WINDOW_HOURS);
    if (insideWindow) {
      const tombstone: Tombstone = {
        id: row.id,
        timestamp: row.timestamp,
        deleted: true,
        deletedAtMs: nowMs,
      };
      await put(tx, STORE.log, tombstone);
      return { kind: 'tombstoned', logRevision: await bumpLogRevision(tx) } satisfies DeleteResult;
    }
    await remove(tx, STORE.log, id);
    return { kind: 'removed', logRevision: await bumpLogRevision(tx) } satisfies DeleteResult;
  });
}

/** §7.8 — reading rows are deletable, with a plain confirmation and no tombstone. */
export function deleteReading(db: IDBDatabase, id: string): Promise<number> {
  return runTransaction(db, HISTORY_SCOPE, 'readwrite', async (tx) => {
    await remove(tx, STORE.readings, id);
    return bumpLogRevision(tx);
  });
}

// ─── §7.9's two clearing operations ─────────────────────────────────────────

/**
 * §7.9 — "clear the record".
 *
 * Its list is a list of **what to remove**, so a store added later is KEPT by
 * default — the conservative direction for a control whose promise is that the
 * prescription survives.
 *
 * `settingsHistory` survives, and that is load-bearing rather than tidy: §11.3
 * allocates the next revision as `max(settingsHistory keys) + 1`, so dropping the
 * store would restart the counter and the next settings change would issue a
 * revision earlier rows had already used.
 *
 * `acks` survives ENTIRELY. Every row in it is bound to something this operation
 * keeps — §10.6's disclaimer to the app, §5.1's ceil gate and §4.5's confirm-once
 * to values still sitting in `settings`. Dropping them would re-gate a man who
 * changed nothing.
 *
 * `meta.backup` is the one exception to "keeps meta", called out here rather than
 * left to §7.7.1, because an implementer reading this alone would keep it — and a
 * timestamp claiming a recent backup would outlive the record it described.
 */
export function clearTheRecord(db: IDBDatabase): Promise<number> {
  return runTransaction(db, HISTORY_SCOPE, 'readwrite', async (tx) => {
    await clear(tx, STORE.log);
    await clear(tx, STORE.readings);
    await put(tx, STORE.meta, {
      key: META_KEY.backup,
      lastJsonExportAtMs: null,
    } satisfies BackupRow);
    // The provenance stamps go with the rows they described.
    return bumpLogRevision(tx, { lastImportAtMs: null, lastLocalInjectionAtMs: null });
  });
}

// ─── §7.7.1's backup counter ────────────────────────────────────────────────

/**
 * §7.7.1 — set on the DOWNLOAD ROUTE ONLY.
 *
 * The browser offers two delivery routes and neither reports what this counter
 * claims. A download resolving proves the download STARTED — the strongest
 * signal the platform offers, and still not completion. Web Share resolving
 * proves only that the sheet was dismissed without error: it does not report
 * which application received the file, or that anything saved it.
 *
 * "A share that resolves is not evidence of a stored copy, and treating it as one
 * is the same false safety claim as counting the readable export — arrived at
 * from the other direction, and less obvious, because the promise really did
 * resolve."
 */
export function recordJsonExport(db: IDBDatabase, nowMs: number): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.meta], 'readwrite', (tx) =>
    put(tx, STORE.meta, { key: META_KEY.backup, lastJsonExportAtMs: nowMs } satisfies BackupRow),
  );
}

// ─── §6.7's dosing note ─────────────────────────────────────────────────────

export function writeDosingHistory(
  db: IDBDatabase,
  row: Omit<DosingHistoryRow, 'key'>,
): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.meta], 'readwrite', (tx) =>
    put(tx, STORE.meta, { ...row, key: META_KEY.dosingHistory } satisfies DosingHistoryRow),
  );
}

// ─── 10a's language ─────────────────────────────────────────────────────────

/**
 * Its OWN write, not part of a settings commit, and that is the point.
 *
 * Choosing a language must not travel through `commitSettings`: that path
 * allocates a prescription revision when a provenance field moves, and a
 * language is not a dosing input. Nor should switching language require saving
 * the prescription — the reader is on the settings screen looking at typefaces,
 * not at ratios, and a control that silently commits the form under them is the
 * kind of side-effect §7.9 ruled against in the other direction.
 *
 * The face rides along even when the language is English, so switching back and
 * forth does not lose the one she was in the middle of comparing.
 */
/**
 * Phase 2 — the reader's own figure for one food.
 *
 * READ-MODIFY-WRITE INSIDE ONE TRANSACTION, not a blind put. The whole map
 * lives in a single `meta` row, so writing it from a value read earlier would
 * lose any calibration another tab set in between — the same class of race
 * §11.3 handles for settings, arriving through a different door.
 *
 * `grams: null` REMOVES the entry rather than storing a zero. Zero is a real
 * carbohydrate figure in this table now, so it cannot double as "no longer
 * calibrated".
 */
/**
 * T31 — record what one vessel holds, as a ratio, with its working.
 *
 * Read-modify-write over the whole map for the same reason `writeCalibration`
 * does it: `meta` holds one row per key, so setting one vessel means rewriting
 * the row that holds them all, and doing it inside the transaction is what
 * stops two tabs clobbering each other.
 *
 * `ratio === null` clears the vessel, and clearing returns the reader to the
 * table's figures — which is the same state as never having calibrated. There
 * is no third state, deliberately: a vessel the app remembers but does not
 * apply is a number nobody can see and everybody would forget.
 */
export function writeVessel(
  db: IDBDatabase,
  vesselId: string,
  entry: {
    readonly ratio: number;
    readonly emptyGrams: number;
    readonly fullGrams: number;
    readonly tared: 'subtracted' | 'scale';
    readonly sourceFoodId: string;
    readonly referenceGrams: number;
    readonly setAt: number;
  } | null,
): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.meta], 'readwrite', async (tx) => {
    const existing = await get<VesselRow>(tx, STORE.meta, META_KEY.vessel);
    const vessels = { ...existing?.vessels };
    if (entry === null) delete vessels[vesselId];
    else vessels[vesselId] = entry;
    return put(tx, STORE.meta, { key: META_KEY.vessel, vessels } satisfies VesselRow);
  });
}

export function writeCalibration(
  db: IDBDatabase,
  foodId: string,
  grams: number | null,
  setAt: number,
): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.meta], 'readwrite', async (tx) => {
    const existing = await get<CalibrationRow>(tx, STORE.meta, META_KEY.calibration);
    const foods = { ...existing?.foods };
    if (grams === null) delete foods[foodId];
    else foods[foodId] = { grams, setAt };
    return put(tx, STORE.meta, { key: META_KEY.calibration, foods } satisfies CalibrationRow);
  });
}

/**
 * Phase 2 — every calibration gone, in one write.
 *
 * The whole map replaced rather than a delete per food: a per-food loop could
 * half-succeed and leave the reader with a table that is partly theirs and
 * partly the reference, with nothing on screen saying which rows are which.
 */
export function clearCalibration(db: IDBDatabase): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.meta], 'readwrite', (tx) =>
    put(tx, STORE.meta, { key: META_KEY.calibration, foods: {} } satisfies CalibrationRow),
  );
}

export function writeDisplay(
  db: IDBDatabase,
  row: Omit<DisplayRow, 'key'>,
): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.meta], 'readwrite', (tx) =>
    put(tx, STORE.meta, { ...row, key: META_KEY.display } satisfies DisplayRow),
  );
}

export function writeLanguage(
  db: IDBDatabase,
  row: Omit<LanguageRow, 'key'>,
): Promise<IDBValidKey> {
  return runTransaction(db, [STORE.meta], 'readwrite', (tx) =>
    put(tx, STORE.meta, { ...row, key: META_KEY.language } satisfies LanguageRow),
  );
}
